import {
  STD_CANVAS_CSS,
  SHEET_PRINT_CSS
} from "@/components/stone-technical-drawing/engine/print/stdCanvasStyles";
import { RECEIPT_PRINT_CSS, buildReceiptHtml } from "./receiptDocument";

const FONT_LINKS = `
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Arsenal:wght@400;700&family=Chakra+Petch:wght@600&display=swap">
`;

const BUDGET_PRINT_CSS = `
  table { width: 100%; }
  body { -webkit-print-color-adjust: exact; }
  .tr-table { border: 1px solid #333; margin: 0px; }
  .bg-texts { font-weight: bold !important; background-color: #302b63 !important; border-radius: 3px; padding: 3px; padding-right: 6px; padding-left: 6px; color: #fff !important; }
  .font-head-title { font-size: 14pt; font-weight: bold; font-family: 'Arsenal', sans-serif; color: #333; }
  .font-head-subtitle { font-size: 12pt; font-family: 'Arsenal', sans-serif; color: #666; }
  .font-title { font-size: 20pt; font-weight: bold; font-family: 'Chakra Petch', sans-serif; color: #333; }
  @page { size: A4; margin: 0.5cm; }
  @media print { .bg-texts { background-color: #302b63 !important; } }
`;

const DELIVERY_NOTE_PRINT_CSS = `
  body { -webkit-print-color-adjust: exact; margin: 0px; padding:0px; width: 100%; display: flex; flex-direction: row; flex-wrap: nowrap; }
  .font-head-title { font-size: 14pt; font-weight: bold; font-family: 'Arsenal', sans-serif; color: #333; }
  .font-head-subtitle { font-size: 12pt; font-family: 'Arsenal', sans-serif; color: #666; }
  .font-title { font-size: 20pt; font-weight: bold; font-family: 'Chakra Petch', sans-serif; color: #333; }
  .page-unbreak { page-break-after: always; background: #333; width: 1100px; display: flex; flex-wrap: nowrap !important; }
  @page { size: landscape; margin: 0.5cm; display: flex; flex-wrap: nowrap; }
`;

const EXIT_NOTE_PRINT_CSS = `
  body { -webkit-print-color-adjust: exact; margin: 0; padding: 0; width: 100%; display: flex; flex-direction: row; flex-wrap: nowrap; font-family: Arial, sans-serif; }
  .font-head-title { font-size: 14pt; font-weight: bold; font-family: Arial, sans-serif; color: #333; }
  .font-head-subtitle { font-size: 10pt; font-family: Arial, sans-serif; color: #666; }
  .font-title { font-size: 20pt; font-weight: bold; font-family: Arial, sans-serif; color: #333; }
  .text-head-title { font-size: 9pt; font-weight: bold; color: #333; }
  .tr-table { border: 1px solid #333; margin: 0; }
  @page { size: landscape; margin: 0.5cm; }
`;

function notifyError(notify, message) {
  if (typeof notify === "function") {
    notify({
      type: "error",
      message,
      timeout: 5000
    });
  }
}

function shouldCloseImmediately(context = {}) {
  return Boolean(context.isDesktop || (context.vuetify && context.vuetify.breakpoint && context.vuetify.breakpoint.lgAndUp));
}

const PRINT_FRAME_ID = "stonepro-print-frame";
const ASSETS_TIMEOUT = 4000;
const FRAME_CLEANUP_TIMEOUT = 120000;

function buildDocument({ title, fonts = "", styles = "", bodyHtml = "" }) {
  return [
    "<!DOCTYPE html>",
    '<html lang="pt-br"><head>',
    '<meta charset="utf-8">',
    `<title>${title}</title>`,
    fonts,
    `<style>${styles}</style>`,
    "</head><body>",
    bodyHtml,
    "</body></html>"
  ].join("");
}

function waitForAssets(frameWindow) {
  return new Promise(resolve => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // nunca deixa a impressao presa esperando fonte/imagem externa
    const timer = setTimeout(done, ASSETS_TIMEOUT);
    const finish = () => {
      clearTimeout(timer);
      done();
    };

    try {
      const doc = frameWindow.document;
      const images = Array.from(doc.images || []);
      const pending = images
        .filter(image => !image.complete)
        .map(
          image =>
            new Promise(imageResolve => {
              image.addEventListener("load", imageResolve, { once: true });
              image.addEventListener("error", imageResolve, { once: true });
            })
        );
      const fontsReady =
        doc.fonts && doc.fonts.ready ? doc.fonts.ready.catch(() => {}) : Promise.resolve();

      Promise.all([...pending, fontsReady]).then(finish, finish);
    } catch (error) {
      finish();
    }
  });
}

function removePrintFrame(frame) {
  if (frame && frame.parentNode) {
    frame.parentNode.removeChild(frame);
  }
}

/**
 * Imprime via iframe oculto de mesma origem. Diferente de window.open, o iframe
 * nao depende de gesto do usuario, entao continua funcionando quando a impressao
 * acontece depois de uma requisicao (ex: recibo emitido apos salvar o pagamento).
 */
function printWithFrame({ title, fonts, styles, bodyHtml }) {
  return new Promise((resolve, reject) => {
    try {
      const previous = document.getElementById(PRINT_FRAME_ID);
      removePrintFrame(previous);

      const frame = document.createElement("iframe");
      frame.id = PRINT_FRAME_ID;
      frame.setAttribute("aria-hidden", "true");
      frame.style.cssText =
        "position: fixed; right: 0; bottom: 0; width: 0; height: 0; border: 0; visibility: hidden;";
      document.body.appendChild(frame);

      const frameWindow = frame.contentWindow;
      if (!frameWindow) {
        removePrintFrame(frame);
        reject(new Error("Nao foi possivel preparar o documento de impressao."));
        return;
      }

      const frameDocument = frameWindow.document;
      frameDocument.open();
      frameDocument.write(buildDocument({ title, fonts, styles, bodyHtml }));
      frameDocument.close();

      waitForAssets(frameWindow).then(() => {
        let cleanupTimer = null;
        const cleanup = () => {
          if (cleanupTimer) clearTimeout(cleanupTimer);
          removePrintFrame(frame);
        };
        try {
          frameWindow.onafterprint = cleanup;
          // fallback: navegadores que nao disparam onafterprint
          cleanupTimer = setTimeout(cleanup, FRAME_CLEANUP_TIMEOUT);
          frameWindow.focus();
          frameWindow.print();
          resolve(true);
        } catch (error) {
          cleanup();
          reject(error);
        }
      });
    } catch (error) {
      reject(error);
    }
  });
}

function printWithPopup({ title, fonts, styles, bodyHtml, width, height, context }) {
  const printWindow = window.open("", "", `width=${width},height=${height}`);
  if (!printWindow) return false;

  printWindow.document.open();
  printWindow.document.write(buildDocument({ title, fonts, styles, bodyHtml }));
  printWindow.document.close();
  printWindow.focus();

  const startPrint = () => {
    printWindow.print();
    if (shouldCloseImmediately(context)) {
      printWindow.close();
    } else {
      printWindow.onafterprint = () => printWindow.close();
    }
  };
  // onload pode ja ter disparado em documentos escritos via document.write
  if (printWindow.document.readyState === "complete") {
    setTimeout(startPrint, 100);
  } else {
    printWindow.onload = startPrint;
  }
  return true;
}

/**
 * Impressao obrigatoria: tenta o iframe e, se falhar, cai para a janela popup.
 */
export function printHtmlDocument({
  title = "Documento",
  styles = "",
  fonts = "",
  bodyHtml = "",
  width = 600,
  height = 600,
  notify,
  context = {}
}) {
  return printWithFrame({ title, fonts, styles, bodyHtml })
    .catch(() => {
      const printed = printWithPopup({
        title,
        fonts,
        styles,
        bodyHtml,
        width,
        height,
        context
      });
      if (!printed) throw new Error("popup-blocked");
      return true;
    })
    .catch(() => {
      notifyError(
        notify,
        "Nao foi possivel abrir a impressao. Libere os pop-ups deste site e tente novamente."
      );
      return false;
    });
}

export function printElement({
  elementId,
  title,
  styles = "",
  fonts = "",
  width = 600,
  height = 600,
  wrapperId = "",
  delay = 200,
  notify,
  context = {}
}) {
  if (!document.getElementById(elementId)) {
    notifyError(notify, "Documento nao encontrado para impressao.");
    return false;
  }

  // o delay preserva o tempo de render do Vue antes de copiar o conteudo
  setTimeout(() => {
    const printNode = document.getElementById(elementId);
    if (!printNode) {
      notifyError(notify, "Documento nao encontrado para impressao.");
      return;
    }
    const inner = printNode.innerHTML;
    const bodyHtml = wrapperId ? `<div id="${wrapperId}">${inner}</div>` : inner;

    printHtmlDocument({
      title,
      styles,
      fonts,
      bodyHtml,
      width,
      height,
      notify,
      context
    });
  }, delay);

  return true;
}

export function printBudget(options = {}) {
  return printElement({
    elementId: options.elementId || "print-html",
    title: options.title || "Proposta Orcamentaria",
    styles: BUDGET_PRINT_CSS,
    fonts: FONT_LINKS,
    width: options.width || 600,
    height: options.height || 600,
    wrapperId: options.wrapperId || "imprimir-conteudo",
    delay: options.delay || 200,
    notify: options.notify,
    context: options.context
  });
}

export function printDeliveryNote(options = {}) {
  return printElement({
    elementId: options.elementId || "print-note",
    title: options.title || "Nota de entrega",
    styles: DELIVERY_NOTE_PRINT_CSS,
    fonts: FONT_LINKS,
    width: options.width || 600,
    height: options.height || 600,
    delay: options.delay || 300,
    notify: options.notify,
    context: options.context
  });
}

export function printTechnicalDrawing(options = {}) {
  return printElement({
    elementId: options.elementId || "print-technical-drawing",
    title: options.title || "Desenho Tecnico",
    styles: `${STD_CANVAS_CSS}\n${SHEET_PRINT_CSS}`,
    width: options.width || 900,
    height: options.height || 700,
    delay: options.delay || 250,
    notify: options.notify,
    context: options.context
  });
}

export function printExitNote(options = {}) {
  return printElement({
    elementId: options.elementId || "print-exit-note",
    title: options.title || "Nota de saida",
    styles: EXIT_NOTE_PRINT_CSS,
    width: options.width || 800,
    height: options.height || 700,
    delay: options.delay || 200,
    notify: options.notify,
    context: options.context
  });
}

/**
 * Recibo de pagamento. O HTML e montado a partir dos dados da venda/pagamento,
 * entao funciona tanto na emissao quanto na reimpressao, sem depender de o
 * modal de fechamento estar aberto na tela.
 */
export function printReceipt(options = {}) {
  const bodyHtml = buildReceiptHtml({
    sale: options.sale,
    receipt: options.receipt,
    user: options.user,
    tenant: options.tenant
  });

  return printHtmlDocument({
    title: options.title || "Recibo",
    styles: RECEIPT_PRINT_CSS,
    fonts: FONT_LINKS,
    bodyHtml,
    width: options.width || 600,
    height: options.height || 600,
    notify: options.notify,
    context: options.context
  });
}

export const saleDocumentPrintCss = {
  budget: BUDGET_PRINT_CSS,
  deliveryNote: DELIVERY_NOTE_PRINT_CSS,
  technicalDrawing: `${STD_CANVAS_CSS}\n${SHEET_PRINT_CSS}`,
  exitNote: EXIT_NOTE_PRINT_CSS,
  receipt: RECEIPT_PRINT_CSS
};
